import { useEffect, useState } from "react";
import { BuyToast, Panel, PurchaseLedger } from "../components/ui";
import ActiveProjectCard from "../components/ActiveProjectCard";
import { fmtCompact, toNum } from "../game/format";
import { useBakeryStore } from "../game/bakeryStore";

function fmtDur(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "—";
  const m = Math.round(ms / 60000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r === 0 ? `${h}h` : `${h}h ${r}m`;
}

function fmtLeft(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "finishing…";
  const s = Math.ceil(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m left`;
  if (m > 0) return `${m}m ${String(sec).padStart(2, "0")}s left`;
  return `${sec}s left`;
}

function livePct(p: { cookieCost: string; progress: string; progressPct?: number; startedAt?: number | null; endsAt?: number | null }, now: number): number {
  if (typeof p.endsAt === "number" && typeof p.startedAt === "number" && p.endsAt > p.startedAt) {
    const dur = p.endsAt - p.startedAt;
    const el = Math.max(0, Math.min(dur, now - p.startedAt));
    return Math.max(0, Math.min(100, (el / dur) * 100));
  }
  if (typeof p.progressPct === "number" && Number.isFinite(p.progressPct)) return Math.max(0, Math.min(100, p.progressPct));
  return 0;
}

export default function ProjectPage() {
  const store = useBakeryStore();
  const snap = store.snapshot;
  const connected = store.connected;
  const [localToast] = useState<string | null>(null);
  const toastNotice = store.buyNotice !== null && (store.buyNotice.kind === "project" || store.buyNotice.kind === "buy") ? store.buyNotice : null;
  const toast = toastNotice ?? (localToast ? { id: -1, text: localToast, kind: "local", ok: false } : null);
  // Live affordability: same total the counter shows
  // (bank + elapsed*CPS + optimistic), never the stale snapshot bank.
  // Projects cost bakery stock PLUS a personal Baker-Credit stake
  // (10% of cookie cost, backend authoritative).
  const cookies = store.shownCookies;
  const liveCredits = store.liveCredits;
  const bakeryLevel = snap?.bakery.level ?? 1;
  const projects = snap?.projects ?? [];
  const done = new Set(projects.filter((p) => p.status === "completed").map((p) => p.id));
  const [now, setNow] = useState(0);
  useEffect(() => {
    setNow(Date.now());
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);
  return (
    <div className="cb-page">
      <Panel className="hl center">
        <h2 className="cb-h">🏗 Projects</h2>
        <p className="dim small" style={{ margin: 0 }} role="status" aria-live="polite">
          {snap !== null ? `Bakery Lv ${bakeryLevel} · ${fmtCompact(cookies)} cookies` : "Loading projects…"}
        </p>
      </Panel>
      <BuyToast notice={toast} />
      <PurchaseLedger history={store.purchaseHistory} kinds={["project"]} />
      {snap !== null && <ActiveProjectCard compact />}
      {snap === null ? (
        <Panel className="center"><p className="small" role="status">Loading…</p></Panel>
      ) : projects.length === 0 ? (
        <Panel className="center"><p className="small" role="status">Bakery projects will appear here once the bakery starts one.</p></Panel>
      ) : (
        <div className="cb-stack">
          {projects.map((p) => {
            const cost = toNum(p.cookieCost);
            // Personal stake mirrors backend: personalCost field when present,
            // else cookieCost / 10 (backend authoritative on ack).
            const personalFee = p.personalCost !== undefined ? toNum(p.personalCost) : Math.max(1, Math.floor(cost / 10));
            const isDone = p.status === "completed";
            const needLevel = p.requiredBakeryLevel ?? 1;
            const levelLocked = bakeryLevel < needLevel;
            const needOne = p.requiresProjectId !== undefined && p.requiresProjectId !== "" ? p.requiresProjectId : null;
            const needAny = p.requiresAny ?? [];
            const prereqLocked = (needOne !== null && !done.has(needOne)) || (needAny.length > 0 && !needAny.some((id) => done.has(id)));
            const rivalLocked = p.exclusiveGroup !== undefined && p.exclusiveGroup !== ""
              ? projects.some((q) => q.id !== p.id && q.exclusiveGroup === p.exclusiveGroup && q.status === "completed")
              : false;
            const cooling = (p.cooldownRemainingMs ?? 0) > 0;
            const requiredSpecs = p.requiredSpecs ?? [];
            const missingSpecs = p.missingSpecs ?? requiredSpecs.filter((s) => !(p.presentSpecs ?? []).includes(s));
            const presentSpecs = p.presentSpecs ?? requiredSpecs.filter((s) => !missingSpecs.includes(s));
            const specLocked = missingSpecs.length > 0;
            const bankAffordable = cookies >= cost;
            const personalAffordable = liveCredits >= personalFee;
            const affordable = bankAffordable && personalAffordable;
            const isActive = p.status === "in_progress";
            const busyElsewhere = projects.some((q) => q.id !== p.id && q.status === "in_progress");
            const pending = store.isPending("project", p.id);
            const canBuy = connected && !isDone && !isActive && !levelLocked && !prereqLocked && !rivalLocked && !specLocked && !cooling && !busyElsewhere && affordable && !pending;
            const pct = livePct(p, now);
            const left = typeof p.endsAt === "number" && Number.isFinite(p.endsAt) ? Math.max(0, p.endsAt - now) : (p.timeLeftMs ?? 0);
            const cap = (s: string): string => s === "" ? s : s.charAt(0).toUpperCase() + s.slice(1);
            // No overwarn: spec text only applies to buildable candidates.
            // Done/active builds never need specs; higher-priority locks
            // (crew busy, bakery level, rival, prereq) hide spec missing/present
            // so the UI never blames specs when they are not the blocker.
            // Spec gate is ALL-together: every listed spec must have at least
            // one ACTIVE baker online at the same time (one person covers only
            // their own current spec, so Eternal Oven needs 2+ people at once).
            const showSpecReq = !isDone && !isActive && requiredSpecs.length > 0;
            const showSpecDetail = showSpecReq && !busyElsewhere && !levelLocked && !rivalLocked && !prereqLocked;
            const specLabel = showSpecReq ? `Needs ${requiredSpecs.map(cap).join(" + ")} together (all active at once)` : null;
            const lockLabel = isDone ? "Completed"
              : isActive ? `Under construction — ${fmtLeft(left)}`
              : busyElsewhere ? "Crew busy — one project at a time"
              : levelLocked ? `Requires Bakery Lv ${needLevel}`
              : rivalLocked ? "Locked — rival branch built"
              : prereqLocked ? `Requires ${needOne ?? needAny.join(" or ")}`
              : specLocked ? `Needs active ${missingSpecs.map(cap).join(" + ")} — all listed specs must be online together`
              : cooling ? `Crew busy — ${fmtDur(p.cooldownRemainingMs ?? 0)} left`
              : !bankAffordable ? `Cost 🍪 ${cost.toLocaleString()}`
              : !personalAffordable ? `Needs 🪙 ${fmtCompact(personalFee)} personal stake`
              : null;
            return (
              <div key={p.id} className={isDone ? "cb-card maxed" : isActive ? (pending ? "cb-card cb-building confirming" : "cb-card cb-building") : pending ? "cb-card confirming" : canBuy ? "cb-card" : "cb-card muted"} aria-busy={pending || undefined}>
                <span className={isActive ? "cb-card-ico cb-crane cookie-pulse" : pending ? "cb-card-ico cookie-pulse" : "cb-card-ico"} aria-hidden="true">🏗</span>
                <div className="cb-card-body">
                  <b>{p.name} {isActive && <span className={pending ? "cb-pill confirming" : "cb-pill"}>🚧 {Math.floor(pct)}% · {fmtLeft(left)}</span>}{!isActive && pending && <span className="cb-pill confirming" role="status">Building…</span>}</b>
                  <small>{p.description}</small>
                  <small className="dim">+{fmtCompact(p.cpsBonus)}/sec · Cost 🍪 {fmtCompact(p.cookieCost)} + 🪙 {fmtCompact(personalFee)} personal · Lv {needLevel}+ · Build {fmtDur(p.durationMs ?? 0)} · Cooldown {fmtDur(p.cooldownMs ?? 0)}</small>
                  {isActive && (
                    <div className="cb-track cb-construct" role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100} aria-label={`${p.name} progress`}>
                      <div className="cb-fill-gold" style={{ width: `${pct}%` }} />
                    </div>
                  )}
                  {specLabel !== null && showSpecDetail && (
                    <small className="dim">
                      {specLabel} · {specLocked ? `missing ${missingSpecs.map(cap).join(", ")}` : `present ${presentSpecs.map(cap).join(", ")}`} · all listed specs must be present simultaneously (one active baker per spec)
                    </small>
                  )}
                  {specLabel !== null && !showSpecDetail && (
                    <small className="dim">{specLabel}</small>
                  )}
                  {isActive && (p.contributors ?? []).length > 0 && (
                    <small className="dim">👷 {(p.contributors ?? []).slice(0, 5).map((c) => `${c.name} ${fmtCompact(c.amount)}`).join(" · ")}</small>
                  )}
                  {lockLabel !== null && <small className={isDone || isActive ? "cb-cost" : "cb-cost poor"}>{isDone ? "Completed" : lockLabel}</small>}
                </div>
                {isDone ? (
                  <span className="cb-max">Done</span>
                ) : isActive ? (
                  <span className={pending ? "cb-inprog confirming" : "cb-inprog"} role={pending ? "status" : undefined} aria-busy={pending || undefined}>{pending ? "Building…" : "Building"}</span>
                ) : pending ? (
                  <button type="button" disabled aria-busy="true" title="Building…" className="cb-buy confirming">
                    Building…
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={!canBuy}
                    title={!connected ? "Not connected — retrying…" : !bankAffordable ? `Need 🍪 ${fmtCompact(p.cookieCost)} bakery stock` : !personalAffordable ? `Need 🪙 ${fmtCompact(personalFee)} personal credits` : "Start construction"}
                    className={canBuy ? "cb-buy" : "cb-buy off"}
                    onClick={() => store.buyProject(p.id)}
                  >
                    {levelLocked || prereqLocked || rivalLocked || specLocked || cooling || busyElsewhere ? "Locked" : affordable ? "Build" : !bankAffordable ? fmtCompact(cost) : `🪙 ${fmtCompact(personalFee)}`}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

    </div>
  );
}
